import { api } from "shared/redux/api"
import { buildListParams, buildProcurementListFilters } from "./procurement"
import { camelizeKeys } from "shared/utils/camelize"
import { BOOK_INVENTORY_TAGS } from "./procurementItems"

// Procurement Manager — Purchase (PO) module.
//
// Mirrors procurementApprovals.js (the real-BE Order module): hits the REAL
// backend under `/private/procurement/*` (the axios base query attaches the
// bearer token only when the URL contains "private"), unwraps the
// `{status/code, message, data}` envelope, and camelCases snake_case keys once
// here so every consumer reads flat camelCase fields.
//
// PO lifecycle (BE is source of truth for buttons — render from allowedActions):
//   create(draft) --submit--> pending --approve--> approved --issue--> issued
//                    |  \info_request--> info_requested --resubmit--> pending
//                    |  \reject--> rejected
//                    \--cancel--> cancelled  (whenever valid)
//
// Versioned under /v1 per the 2026-08-2x procurement path cleanup.
const BASE = "/private/procurement/v1"

const unwrap = (response) => camelizeKeys(response?.data)

const proposalTags = (orderRequestId, purchaseId) => [
  { type: "VENDOR_PROPOSAL", id: orderRequestId || "ALL" },
  { type: "VENDOR_PROPOSAL", id: "ALL" },
  { type: "PURCHASE_VENDOR_PROPOSAL", id: purchaseId || "ALL" },
  { type: "PURCHASE_VENDOR_PROPOSAL", id: "ALL" },
  ...(purchaseId ? [{ type: "PURCHASE", id: purchaseId }] : []),
  ...(orderRequestId ? [{ type: "ORDER-REVIEW", id: orderRequestId }] : []),
]

// Buttons render ENTIRELY from `allowedActions` — never inferred from status
// (see PurchaseActionBar.js). Compat safety net: a `draft` PO is always
// submittable (create --submit--> pending is the one universal first step),
// so if the backend's `allowedActions` ever comes back without "submit" for
// a draft row — empty array, or the field omitted entirely — add it back
// rather than silently rendering an action bar with nothing in it.
const withDraftSubmitCompat = (row) => {
  if (!row || typeof row !== "object") return row
  const actions = Array.isArray(row.allowedActions) ? row.allowedActions : []
  if (row.status === "draft" && !actions.includes("submit")) {
    return { ...row, allowedActions: [...actions, "submit"] }
  }
  return actions === row.allowedActions ? row : { ...row, allowedActions: actions }
}

// Detail nests the record under `header`, alongside `linkedOrders[]`,
// `approvalProgress[]`, `actionLog[]`, `paymentDetail[]` and `allowedActions[]`.
// Flatten `header` up so callers read `po.poCode`, `po.total`, `po.status`,
// and keep the lists at top level. No-ops if a response ever arrives already
// flat (no `header` key).
//
// Real header fields (confirmed 2026-08-13): id, poCode, status, region,
// budgetCategoryId, budgetCategoryName, vendorId, vendorName, paymentMethod,
// totalAmount, sunPaymentStatus, createdBy, approvalRuleId, ruleVersion,
// currentStepNo, approvalError, createdAt. No `center`/`department` — those
// render as "—" in the detail page until the backend adds them.
const flattenPurchaseDetail = (data) => {
  if (!data) return data
  const header = data.header || null
  const flatHeader = header
    ? {
        ...header,
        id: header.id,
        poCode: header.poCode || header.code,
        total: header.total ?? header.totalAmount,
        supplierName: header.supplierName || header.vendorName,
        categoryName: header.categoryName || header.category || header.budgetCategoryName,
        paidStatus: header.paidStatus || header.sunPaymentStatus,
      }
    : {}
  return withDraftSubmitCompat({
    ...data,
    ...flatHeader,
    linkedOrders: data.linkedOrders || [],
    paymentDetail: data.paymentDetail || [],
    approvalProgress: data.approvalProgress || [],
    actionLog: data.actionLog || [],
    allowedActions: data.allowedActions || [],
    documents: data.documents || [],
    detachedOrders: data.detachedOrders || [],
  })
}
const unwrapPurchaseDetail = (response) => flattenPurchaseDetail(unwrap(response))

// After any lifecycle mutation the detail + list must both refresh so the
// status badge, stepper and allowed_actions re-render from the server truth.
// Also invalidates cross-domain caches that read a PO's status/linkage: the
// Order Review list (a linked order's row can show PO-derived state) and the
// lifecycle card on My Orders (F6) — without these, a page opened before an
// issue/receive/cancel keeps showing the pre-action state until its cache
// naturally expires (keepUnusedDataFor, default 60s).
const invalidatePurchase = (_r, _e, arg) => {
  const id = typeof arg === "object" ? arg.id : arg
  return [
    { type: "PURCHASE", id },
    { type: "PURCHASE", id: "LIST" },
    { type: "ORDER-REVIEW", id: "ALL-LIST" },
    { type: "ORDER-LIFECYCLE", id: "ALL" },
  ]
}

const procurementPurchasesApi = api.injectEndpoints({
  endpoints: (build) => ({
    // GET /purchases?...filters&page=&size= — org-wide PO list.
    // `awaitingMe: true` → GET /purchases?awaiting_me=true quick filter — the
    // wire param is snake_case even though every other filter here is a
    // single word, so map it explicitly instead of relying on buildListParams.
    // Shared filter set (from/to date, category, region, center, item,
    // supplier) is mapped by buildProcurementListFilters. NOTE: the
    // `payable: true` branch (ToPayListContainer) is a different BE query
    // path that only reads region/category/supplier/q — it does not honor
    // department/center/item/from_date/to_date even though they're sent
    // here, so don't add those filters to that screen expecting them to work.
    getPurchases: build.query({
      query: ({ awaitingMe, ...rest } = {}) => ({
        url: `${BASE}/purchases`,
        params: buildProcurementListFilters({ ...rest, awaiting_me: awaitingMe }),
      }),
      transformResponse: unwrap,
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map((p) => ({ type: "PURCHASE", id: p.id ?? p.poCode })),
              { type: "PURCHASE", id: "LIST" },
            ]
          : [{ type: "PURCHASE", id: "LIST" }],
    }),

    // GET /purchases/{id} — header + linked_orders[] + payment_detail[] +
    // allowed_actions[].
    getPurchase: build.query({
      query: (id) => ({ url: `${BASE}/purchases/${id}` }),
      transformResponse: unwrapPurchaseDetail,
      providesTags: (_r, _e, id) => [{ type: "PURCHASE", id }],
    }),

    // POST /purchases — create a draft PO from exactly ONE source: a single
    // approved order (orderRequestId) or a single group order (groupOrderId) —
    // never both. Two amount modes:
    //   - full amount: { orderRequestId|groupOrderId, vendorId, paymentMethod }
    //   - split by a chosen vendor proposal: { orderRequestId|groupOrderId,
    //     vendorProposalId, vendorId } — vendorProposalId must belong to the
    //     same source and vendorId must match that proposal's supplierId.
    // Returns the draft PO detail (header.orderRequestId / header.groupOrderId
    // tells which source it is; linkedOrders lists 1 order, or every order in
    // the group). For a group source, member orders that aren't approved yet
    // are split off the group instead of blocking PO creation — they show up
    // in `detachedOrders` (empty array otherwise) and are excluded from
    // linkedOrders/totalAmount. 400 on missing/mixed source, unknown ids, or
    // (for a group source) orders that don't share the same category+region.
    // 409 when the source has NO approved member at all, or the requested
    // amount would exceed what's still unallocated — surfaced verbatim by the
    // container.
    //
    // A 4th source, `directOrder` (2026-09 barrier-removal feature): a
    // {title, budgetCategoryId, centerId, departmentId?, region?, reason?,
    // lines[]} object, mutually exclusive with orderRequestId/groupOrderId.
    // BE builds a hidden, already-`approved` "implicit order" from it —
    // requires the chosen category (and any itemId'd line) to have
    // mustHaveOrder=false, else 409. Response shape is identical to the
    // order/group case; header.orderRequestId points at the hidden order.
    //
    // `newSupplier` ({name, taxCode, email?}, 2026-09) — alternative to
    // `vendorId`, exactly one of the two. BE creates the supplier inline
    // (same SupplierService.create() POST /suppliers uses; 409 on a
    // duplicate taxCode) and header.vendorId/vendorName on the response is
    // that new supplier. 400 if sent together with vendorProposalId — a
    // proposal is always tied to an existing supplier already.
    createPurchase: build.mutation({
      // `lineIds` (optional): only these source lines go on the PO — total and
      // copied proposal quotes follow them. Omitted = every free line. 400 if
      // a line isn't the source's / the proposal doesn't quote them; 409 if a
      // line is already on another live PO.
      query: ({ orderRequestId, groupOrderId, directOrder, vendorProposalId, vendorId, newSupplier, paymentMethod, lineIds }) => ({
        url: `${BASE}/purchases`,
        method: "POST",
        body: { orderRequestId, groupOrderId, directOrder, vendorProposalId, vendorId, newSupplier, paymentMethod, lineIds },
      }),
      transformResponse: unwrapPurchaseDetail,
      invalidatesTags: [
        { type: "PURCHASE", id: "LIST" },
        { type: "ORDER-REVIEW", id: "ALL-LIST" },
        { type: "ORDER-LIFECYCLE", id: "ALL" },
      ],
    }),

    // PUT /purchases/{id} — edit a PO while it's a Draft, or answer an
    // info_request (draft|info_requested). NEW endpoint — no contract yet;
    // mirrors updateOrderRequest/updatePaymentRequest's shape (PUT the same
    // fields create takes) until BE confirms the real one. Vendor proposal
    // isn't editable here — only full-amount fields.
    updatePurchase: build.mutation({
      query: ({ id, vendorId, paymentMethod }) => ({
        url: `${BASE}/purchases/${id}`,
        method: "PUT",
        body: { vendorId, paymentMethod },
      }),
      transformResponse: unwrapPurchaseDetail,
      invalidatesTags: invalidatePurchase,
    }),

    // GET /purchases/{id}/price-readiness — is every line of the PO's selected
    // proposal priced? { vendorId, vendorName, proposalId, ready, issue
    // (NO_VENDOR|null), rows[]: { itemId | orderRequestLineId, itemName,
    // quantity, status (PRICED|AUTO_FILLABLE|MISSING), proposedAmount,
    // suggestedUnitPrice, suggestedAmount, missingReason } }. Tagged as the PO
    // itself so every PO / proposal mutation refreshes it.
    getPurchasePriceReadiness: build.query({
      query: (id) => ({ url: `${BASE}/purchases/${id}/price-readiness` }),
      transformResponse: unwrap,
      providesTags: (_r, _e, id) => [{ type: "PURCHASE", id }],
    }),

    // GET /order-requests/{id}/price-readiness — same shape, a preview before
    // the PO exists. vendorId defaults to the OR's selected proposal's vendor,
    // lineIds to every line.
    getOrderPriceReadiness: build.query({
      query: ({ id, vendorId, lineIds }) => ({
        url: `${BASE}/order-requests/${id}/price-readiness`,
        params: buildListParams({
          vendor_id: vendorId,
          line_ids: lineIds?.length ? lineIds.join(",") : undefined,
        }),
      }),
      transformResponse: unwrap,
    }),

    // PUT /purchases/{id}/proposal-prices — write the missing prices onto the
    // PO's selected proposal (created if none). Each item: exactly one of
    // itemId / orderRequestLineId, exactly one of unitPrice / proposedAmount.
    // Returns the PO's price-readiness after the write. 409 once the PO is
    // approved or still has no vendor.
    updatePurchaseProposalPrices: build.mutation({
      query: ({ id, items, taxCode }) => ({
        url: `${BASE}/purchases/${id}/proposal-prices`,
        method: "PUT",
        // taxCode (≤ 20) = the vendor's tax code quoted on the proposal; omitted keeps it.
        body: { items, taxCode: taxCode || undefined },
      }),
      transformResponse: unwrap,
      invalidatesTags: (_r, _e, { id }) => [
        { type: "PURCHASE", id },
        { type: "PURCHASE", id: "LIST" },
        { type: "PURCHASE_VENDOR_PROPOSAL", id },
      ],
    }),

    // GET /purchases/{id}/budget-impact — read-only: PO amount vs the budget it
    // holds (OR estimate + earlier adjusts). { purchaseAmount, coveredAmount,
    // differenceAmount (+ = needs more), adjustRequired (issue blocked),
    // releasable, canAdjust, message, tracks[] (category × budget center:
    // availableBefore → availableAfter, enough), orders[] (OR totalBefore →
    // totalAfter), rows[], budgetCategories[] }.
    getPurchaseBudgetImpact: build.query({
      query: (id) => ({ url: `${BASE}/purchases/${id}/budget-impact` }),
      transformResponse: unwrap,
      providesTags: (_r, _e, id) => [{ type: "PURCHASE", id }],
    }),

    // GET /internal-stock/availability?orderRequestId= | groupOrderId= | purchaseId=
    // (exactly one) → [{ warehouseCenterId, supplierId, warehouseName, enough,
    // stockIssued, items: [{ itemId, itemName, neededQty, onHandQty, shortQty }] }]
    // for warehouse 9999 then 10000. With an order/group source, `lineIds` limits
    // neededQty to the lines going on the PO (omitted = every line not on a PO yet).
    getInternalStockAvailability: build.query({
      query: ({ lineIds, ...params }) => ({
        url: `${BASE}/internal-stock/availability`,
        params: { ...params, lineIds: lineIds?.length ? lineIds.join(",") : undefined },
      }),
      transformResponse: unwrap,
    }),

    // GET /purchases/{id}/order-line-price-changes — read-only: what each OR line
    // becomes when this PO is approved at its last step. { ready, oldTotal,
    // newTotal, difference, lines: [{ orderRequestId, orCode, orderRequestLineId,
    // itemName, centerId, period, quantity, oldUnitPrice, newUnitPrice,
    // oldAmount, newAmount, difference }] }. ready=false while the proposal is
    // not fully priced.
    getPurchaseOrderLinePriceChanges: build.query({
      query: (id) => ({ url: `${BASE}/purchases/${id}/order-line-price-changes` }),
      transformResponse: unwrap,
      providesTags: (_r, _e, id) => [{ type: "PURCHASE", id }],
    }),

    // POST /purchases/{id}/budget-adjust — { note? }. Applies the impact above
    // (updates the source ORs' totals) and returns it re-read. PO owner or
    // procurement staff only (403); PO must be draft/pending/info_requested/
    // approved. 409 when a track lacks available budget, or the PO is issued.
    adjustPurchaseBudget: build.mutation({
      query: ({ id, note }) => ({
        url: `${BASE}/purchases/${id}/budget-adjust`,
        method: "POST",
        body: { note: note || undefined },
      }),
      transformResponse: unwrap,
      invalidatesTags: (_r, _e, { id }) => [
        { type: "PURCHASE", id },
        { type: "PURCHASE", id: "LIST" },
        { type: "ORDER-REVIEW", id: "ALL-LIST" },
        { type: "ORDER-LIFECYCLE", id: "ALL" },
      ],
    }),

    // POST /purchases/{id}/submit — draft → pending. REQUIRED before approve.
    submitPurchase: build.mutation({
      query: (id) => ({ url: `${BASE}/purchases/${id}/submit`, method: "POST" }),
      transformResponse: unwrapPurchaseDetail,
      invalidatesTags: invalidatePurchase,
    }),

    // POST /purchases/{id}/actions — approver decision. Body: { action, note,
    // deadline } where action ∈ approve | reject | info_request. `deadline` is
    // only meaningful for info_request: optional, "yyyy-MM-dd" (BE defaults to
    // end of day VN time) or "yyyy-MM-ddTHH:mm" for an exact cutoff — omitted
    // entirely when not set. Only offered when present in allowed_actions[].
    purchaseAction: build.mutation({
      query: ({ id, action, note, deadline }) => ({
        url: `${BASE}/purchases/${id}/actions`,
        method: "POST",
        body: { action, note, deadline },
      }),
      transformResponse: unwrapPurchaseDetail,
      invalidatesTags: invalidatePurchase,
    }),

    // POST /purchases/{id}/resubmit — info_requested → pending.
    resubmitPurchase: build.mutation({
      query: ({ id, note }) => ({
        url: `${BASE}/purchases/${id}/resubmit`,
        method: "POST",
        body: { note },
      }),
      transformResponse: unwrapPurchaseDetail,
      invalidatesTags: invalidatePurchase,
    }),

    // POST /purchases/{id}/issue — approved → issued. Budget is handled by BE;
    // FE just refetches and reflects the new status.
    issuePurchase: build.mutation({
      query: (id) => ({ url: `${BASE}/purchases/${id}/issue`, method: "POST" }),
      transformResponse: unwrapPurchaseDetail,
      invalidatesTags: invalidatePurchase,
    }),

    // POST /purchases/{id}/receive — record a goods receipt. issued → receiving
    // (complete=false, partial) or → completed (complete=true, terminal — no
    // allowedActions left; not blocked by unreceived lines, stock is added by
    // the quantities actually received). Multipart: file is optional (a status-only ping
    // with no new document is valid), note optional, complete required.
    // 409 if the PO isn't issued/receiving yet, or is already completed.
    receivePurchase: build.mutation({
      query: ({ id, file, note, complete, notifyTargets }) => {
        const formData = new FormData()
        if (file) formData.append("file", file)
        if (note) formData.append("note", note)
        formData.append("complete", complete ? "true" : "false")
        // Only has an effect with complete=true: the order requester is always
        // emailed, `notifyTargets` (vendor, pro_manager) adds recipients.
        if (complete && notifyTargets) formData.append("notifyTargets", notifyTargets)
        return { url: `${BASE}/purchases/${id}/receive`, method: "POST", body: formData }
      },
      transformResponse: unwrapPurchaseDetail,
      // Completing an internal-transfer PO closes its ORs on the BE, so every order view refetches.
      invalidatesTags: (r, e, arg) => [
        ...invalidatePurchase(r, e, arg),
        { type: "GRN", id: arg.id },
        ...BOOK_INVENTORY_TAGS,
        "ORDER-REVIEW",
      ],
    }),

    // POST /purchases/{id}/send-to-vendor and /resend-to-vendor — email the PO to
    // the supplier (no body). send: 400 supplier has no email, 409 already sent
    // (use resend). resend: 429 within 5 min of the last send ("please wait N
    // minutes"). Both resolve to { purchaseId, message, resent, sentTo, sentAt,
    // nextResendAllowedAt }.
    sendPurchaseToVendor: build.mutation({
      query: (id) => ({ url: `${BASE}/purchases/${id}/send-to-vendor`, method: "POST" }),
      transformResponse: unwrap,
      invalidatesTags: invalidatePurchase,
    }),
    resendPurchaseToVendor: build.mutation({
      query: (id) => ({ url: `${BASE}/purchases/${id}/resend-to-vendor`, method: "POST" }),
      transformResponse: unwrap,
      invalidatesTags: invalidatePurchase,
    }),

    // POST /purchases/{id}/cancel — → cancelled. Body: { reason } (required,
    // non-blank). 409 when the PO is completed/rejected or still has unfinished
    // payment requests (the message lists their PR codes). The reason comes
    // back as header.reason on the detail and on every list row.
    cancelPurchase: build.mutation({
      query: ({ id, reason }) => ({
        url: `${BASE}/purchases/${id}/cancel`,
        method: "POST",
        body: { reason },
      }),
      transformResponse: unwrapPurchaseDetail,
      invalidatesTags: invalidatePurchase,
    }),

    // GET /purchases/suppliers — all suppliers/vendors across the org.
    // NOTE: named distinctly from procurement.js's `getSuppliers` (legacy PR
    // flow, /private/procurement/suppliers) — RTK Query endpoint names are
    // global across every `injectEndpoints` call on the same `api` instance,
    // so a same-named endpoint here would silently resolve to that one
    // instead (overrideExisting: false means whichever loads first wins).
    getPurchaseSuppliers: build.query({
      query: () => ({ url: `${BASE}/purchases/suppliers` }),
      transformResponse: unwrap,
      providesTags: [{ type: "SUPPLIER", id: "LIST" }],
    }),

    // GET /orders/vendor-proposals?orderRequestId=X — 1 row per supplier
    // that has quoted this order, each with `items: [{ itemId, itemName,
    // proposedAmount, note }]`, `isSelected`, `pin`/`url` (4-digit pin, rotates
    // every time the supplier submits — share `url` with the supplier, never
    // the bare pin), `expiredAt` (the url's deadline — past it, submit 410s),
    // and `supplier` (full SupplierResponse object: name/taxCode/bankName/
    // bankAccount/region/email/budgetCategoryId/isActive — null if the
    // supplier was deleted; `supplierName` is kept alongside for compat).
    // NOTE: the BE filter only supports orderRequestId today — there's no
    // groupOrderId param yet, so a group source's proposals must be fetched
    // with no filter (returns everything, including every order's proposals)
    // and matched client-side on `p.groupOrderId`.
    getVendorProposals: build.query({
      query: (orderRequestId) => ({
        url: `${BASE}/orders/vendor-proposals`,
        params: orderRequestId ? { orderRequestId } : undefined,
      }),
      transformResponse: unwrap,
      providesTags: (_r, _e, orderRequestId) => [
        { type: "VENDOR_PROPOSAL", id: orderRequestId || "ALL" },
      ],
    }),

    // GET /orders/vendor-proposals?groupOrderId= — a Group Order's proposals,
    // same row shape as getVendorProposals. Tagged "ALL" so the create /
    // select / revoke / preferred mutations (which invalidate "ALL") refresh it.
    getGroupVendorProposals: build.query({
      query: (groupOrderId) => ({ url: `${BASE}/orders/vendor-proposals`, params: { groupOrderId } }),
      transformResponse: unwrap,
      providesTags: (_r, _e, groupOrderId) => [
        { type: "VENDOR_PROPOSAL", id: `GROUP-${groupOrderId}` },
        { type: "VENDOR_PROPOSAL", id: "ALL" },
      ],
    }),

    // GET /group-orders/{id}/lines — lines to quote on a Group Order RFQ:
    // catalog items summed per itemId (lineId null), off-catalog lines one
    // row each with a lineId.
    getGroupOrderLines: build.query({
      query: (groupOrderId) => ({ url: `${BASE}/group-orders/${groupOrderId}/lines` }),
      transformResponse: unwrap,
    }),

    // POST /orders/vendor-proposals — single entry point for both ways staff
    // start a vendor proposal:
    //   - "send an RFQ" (CreateProposalDrawer): `items[].proposedAmount` left
    //     unset — blank checklist rows, price comes later from the
    //     supplier's own public submit. Multiple suppliers + brand-new
    //     suppliers (created as permanent Supplier rows server-side, 409 on a
    //     taxCode that already exists) can go in one call, sharing one
    //     title/terms/expiredAt/pinEnabled.
    //   - "enter prices yourself" (NewProposalForm): `items[].proposedAmount`
    //     filled in directly, `pinEnabled: false` (no link needed since
    //     staff already has the price), `supplierIds` has exactly one id —
    //     items/prices apply identically to every supplier in `supplierIds`,
    //     so a call with 2+ *differently priced* suppliers is wrong; each
    //     must be its own call.
    // `title`/`terms` (≥1)/`expiredAt` (> now) are ALWAYS required, even for
    // the direct-entry case — callers there just fill in simple placeholder
    // values (a real link/PIN is moot, but the RFQ record still needs them).
    // OR/PO only: `terms` (one line ≤ 200 chars each) replaces the scope's
    // requirement set; once a vendor has responded it is locked and different
    // `terms` → 409, the same ones still pass.
    // `supplierIds` + `newSuppliers` together must total >= 1.
    // Response is an ARRAY, one element per resulting supplier header (same
    // per-header shape as `getVendorProposals`' rows).
    createVendorProposalRequest: build.mutation({
      query: ({
        orderRequestId,
        groupOrderId,
        supplierIds,
        newSuppliers,
        title,
        terms,
        requiredDocuments,
        deliveryLocation,
        deliveryDate,
        noteForVendor,
        contactName,
        contactEmail,
        contactPhone,
        pinEnabled,
        items,
        supplierItems,
        expiredAt,
        deliveryFee,
      }) => ({
        url: `${BASE}/orders/vendor-proposals`,
        method: "POST",
        body: {
          orderRequestId,
          groupOrderId,
          supplierIds,
          newSuppliers: (newSuppliers || []).map(({ name, taxCode, email }) => ({
            name,
            taxCode,
            email: email || undefined,
          })),
          title,
          terms,
          requiredDocuments: (requiredDocuments || []).map(({ name, mandatory }) => ({
            name,
            mandatory: !!mandatory,
          })),
          deliveryLocation: deliveryLocation || undefined,
          deliveryDate: deliveryDate || undefined,
          noteForVendor: noteForVendor || undefined,
          contactName: contactName || undefined,
          contactEmail: contactEmail || undefined,
          contactPhone: contactPhone || undefined,
          pinEnabled,
          // Exactly one of itemId (catalog) / orderRequestLineId (off-catalog line).
          items: (items || []).map(({ itemId, orderRequestLineId, proposedAmount, note, warrantyMonths, warrantyNote }) => ({
            itemId: itemId ?? undefined,
            orderRequestLineId: orderRequestLineId ?? undefined,
            proposedAmount: proposedAmount ?? undefined,
            note: note || undefined,
            // Omitted = BE keeps the saved value.
            warrantyMonths: warrantyMonths ?? undefined,
            warrantyNote: warrantyNote || undefined,
          })),
          // B4: per-supplier subset of `items`; a supplier without an entry quotes every item.
          supplierItems: supplierItems?.length
            ? supplierItems.map(({ supplierId, items: subset }) => ({
                supplierId,
                items: subset.map(({ itemId, orderRequestLineId }) => ({
                  itemId: itemId ?? undefined,
                  orderRequestLineId: orderRequestLineId ?? undefined,
                })),
              }))
            : undefined,
          // Shown on the proposal/PO only — never part of totalAmount.
          deliveryFee: deliveryFee ?? undefined,
          expiredAt,
        },
      }),
      transformResponse: unwrap,
      // PROPOSAL_COMPARISON: `terms` rewrite the OR's requirement set (its `criteria`).
      invalidatesTags: (_r, _e, { orderRequestId }) => [
        { type: "VENDOR_PROPOSAL", id: orderRequestId || "ALL" },
        { type: "VENDOR_PROPOSAL", id: "ALL" },
        ...(orderRequestId
          ? [
              { type: "ORDER-REVIEW", id: orderRequestId },
              { type: "PROPOSAL_COMPARISON", id: `order_request-${orderRequestId}` },
            ]
          : []),
      ],
    }),

    // POST /orders/vendor-proposals/{id}/revoke — no body. Sets expiredAt=now
    // and rotates the PIN so the old link dies (403 wrong PIN / 410 expired);
    // with pinEnabled=false only the expiry blocks it. Same owner/approver
    // permission and same source-agnostic path as `select` (order, group, PO).
    // Response: the revoked proposal (new pin/url, expiredAt = revoke time).
    // Tag args mirror selectVendorProposal.
    revokeVendorProposal: build.mutation({
      query: ({ id }) => ({
        url: `${BASE}/orders/vendor-proposals/${id}/revoke`,
        method: "POST",
      }),
      transformResponse: unwrap,
      invalidatesTags: (_r, _e, { orderRequestId, purchaseId }) => [
        { type: "VENDOR_PROPOSAL", id: orderRequestId || "ALL" },
        { type: "VENDOR_PROPOSAL", id: "ALL" },
        { type: "PURCHASE_VENDOR_PROPOSAL", id: purchaseId || "ALL" },
        { type: "PURCHASE_VENDOR_PROPOSAL", id: "ALL" },
        ...(purchaseId ? [{ type: "PURCHASE", id: purchaseId }] : []),
        ...(orderRequestId ? [{ type: "ORDER-REVIEW", id: orderRequestId }] : []),
      ],
    }),

    // Same permission + response shape as revoke, but issues a fresh pin/url and
    // a new expiredAt (BE default now+7d) so the vendor can edit again.
    reactivateVendorProposal: build.mutation({
      query: ({ id, expiredAt }) => ({
        url: `${BASE}/orders/vendor-proposals/${id}/reactivate`,
        method: "POST",
        body: { expiredAt: expiredAt || null },
      }),
      transformResponse: unwrap,
      invalidatesTags: (_r, _e, { orderRequestId, purchaseId }) => proposalTags(orderRequestId, purchaseId),
    }),

    // Overwrites the whole text; blank clears it.
    updateVendorProposalCommunicationHistory: build.mutation({
      query: ({ id, communicationHistory }) => ({
        url: `${BASE}/orders/vendor-proposals/${id}/communication-history`,
        method: "PUT",
        body: { communicationHistory },
      }),
      transformResponse: unwrap,
      invalidatesTags: (_r, _e, { orderRequestId, purchaseId }) => proposalTags(orderRequestId, purchaseId),
    }),

    // Only vendor submits through the public link create a revision; staff price
    // edits don't. Each `snapshot` is the full proposal at that submit.
    getVendorProposalRevisions: build.query({
      query: (id) => ({ url: `${BASE}/orders/vendor-proposals/${id}/history` }),
      transformResponse: unwrap,
      providesTags: (_r, _e, id) => [{ type: "VENDOR_PROPOSAL_REVISION", id }],
    }),

    // POST /orders/vendor-proposals/{id}/select — requester/staff explicitly
    // picks the winning proposal out of the (already-submitted) quotes on an
    // order OR a PO — confirmed 2026-09: `{id}` (the PROPOSAL header id, one
    // row per supplier) alone resolves the source (the URL always says
    // "orders" regardless — BE looks it up via the proposal's own siblings,
    // not the path). Body: { reason } (required, 400 if blank). Response is
    // the FULL sibling list for that same source (every proposal on the same
    // order/PO, not just the chosen one) — the picked one comes back
    // isSelected=true + selectionReason/selectedBy/selectedAt, every other
    // sibling isSelected=false. 404 unknown id, 403 if the caller isn't the
    // order/PO's createdBy.
    //
    // Callers pass `orderRequestId` (order-sourced) or `purchaseId`
    // (PO-sourced) — whichever is relevant — purely for cache invalidation
    // (the request body itself never needs a source id). Invalidating
    // VENDOR_PROPOSAL/PURCHASE_VENDOR_PROPOSAL lets the proposals list
    // refetch and re-render every supplier's row from server truth; also
    // invalidating PURCHASE (for a PO-sourced select) picks up whatever the
    // backend recalculates on the PO itself (total/budget) as a result of
    // the new selection.
    //
    // ORDER-REVIEW also needs invalidating (order-sourced only): the order
    // detail/review pages (OrderDetailContainer.js, OrderReviewContainer.js)
    // don't call getVendorProposals themselves — they read
    // `order.vendorProposals` straight off getOrderReview's own response, so
    // without this they kept showing the pre-select isSelected state until
    // that page's cache happened to expire on its own.
    // POST /proposal-preferred — mark / unmark one quote line (item × vendor)
    // as preferred. Independent of isSelected; any number of vendors can be
    // preferred for the same item. Response: data: true.
    setProposalPreferred: build.mutation({
      query: ({ vendorProposalItemId, preferred }) => ({
        url: `${BASE}/proposal-preferred`,
        method: "POST",
        body: { vendorProposalItemId, preferred },
      }),
      invalidatesTags: (_r, _e, { orderRequestId, purchaseId }) => [
        { type: "VENDOR_PROPOSAL", id: orderRequestId || "ALL" },
        { type: "VENDOR_PROPOSAL", id: "ALL" },
        { type: "PURCHASE_VENDOR_PROPOSAL", id: purchaseId || "ALL" },
        ...(orderRequestId ? [{ type: "ORDER-REVIEW", id: orderRequestId }] : []),
        ...(purchaseId ? [{ type: "PURCHASE", id: purchaseId }] : []),
      ],
    }),

    // GET /proposal-comparisons?scopeType=&scopeId= — what the compare modal
    // opens with: { proposals (same shape as getVendorProposals, incl.
    // requirementAnswers { [criteriaId]: text } + tickedCriteria (null = never
    // scored)), criteria[] { id, name, orderNo } (OR/PO requirement set, [] for
    // group), comparisonNotes[] (old → new), comparisonNoteText (null if none) }.
    // scopeType: order_request | group_order | purchase.
    getProposalComparison: build.query({
      query: ({ scopeType, scopeId }) => ({ url: `${BASE}/proposal-comparisons`, params: { scopeType, scopeId } }),
      transformResponse: unwrap,
      providesTags: (_r, _e, { scopeType, scopeId }) => [{ type: "PROPOSAL_COMPARISON", id: `${scopeType}-${scopeId}` }],
    }),

    // PUT /proposal-requirements — replace an OR/PO's whole requirement set
    // without sending an RFQ; BE also rewrites `terms` on every proposal of
    // that scope. scopeType: order_request | purchase (group → 400).
    // Response: { scopeType, scopeId, locked, criteria[] } — same shape as the
    // OR/PO detail's `proposalRequirements`. 409 once locked (or PO approved).
    updateProposalRequirements: build.mutation({
      query: ({ scopeType, scopeId, terms }) => ({
        url: `${BASE}/proposal-requirements`,
        method: "PUT",
        body: { scopeType, scopeId, terms },
      }),
      transformResponse: unwrap,
      invalidatesTags: (_r, _e, { scopeType, scopeId }) => [
        { type: "PROPOSAL_COMPARISON", id: `${scopeType}-${scopeId}` },
        ...(scopeType === "purchase"
          ? [
              { type: "PURCHASE", id: scopeId },
              { type: "PURCHASE_VENDOR_PROPOSAL", id: scopeId },
            ]
          : [
              { type: "ORDER-REVIEW", id: scopeId },
              { type: "VENDOR_PROPOSAL", id: scopeId },
            ]),
      ],
    }),

    // POST /proposal-comparisons — one transaction: change the winner (when
    // selectedProposalId differs; null = keep), set preferred flags, append
    // one note round. `reason` is required only when the winner changes.
    // Response: same as getProposalComparison. Replaces /select and
    // /proposal-preferred for the compare modal.
    saveProposalComparison: build.mutation({
      query: ({ scopeType, scopeId, selectedProposalId, reason, preferred, scoring, noteText }) => ({
        url: `${BASE}/proposal-comparisons`,
        method: "POST",
        body: {
          scopeType,
          scopeId,
          selectedProposalId: selectedProposalId ?? null,
          reason: reason || undefined,
          preferred: preferred || [],
          scoring,
          noteText,
        },
      }),
      transformResponse: unwrap,
      invalidatesTags: (_r, _e, { scopeType, scopeId }) => [
        { type: "PROPOSAL_COMPARISON", id: `${scopeType}-${scopeId}` },
        { type: "VENDOR_PROPOSAL", id: "ALL" },
        { type: "PURCHASE_VENDOR_PROPOSAL", id: "ALL" },
        ...(scopeType === "order_request"
          ? [
              { type: "VENDOR_PROPOSAL", id: scopeId },
              { type: "ORDER-REVIEW", id: scopeId },
              { type: "ORDER-REVIEW", id: "MY-LIST" },
              { type: "ORDER-REVIEW", id: "ALL-LIST" },
            ]
          : []),
        ...(scopeType === "group_order" ? [{ type: "VENDOR_PROPOSAL", id: `GROUP-${scopeId}` }] : []),
        ...(scopeType === "purchase"
          ? [
              { type: "PURCHASE_VENDOR_PROPOSAL", id: scopeId },
              { type: "PURCHASE", id: scopeId },
              { type: "PURCHASE", id: "LIST" },
            ]
          : []),
      ],
    }),

    selectVendorProposal: build.mutation({
      query: ({ id, reason }) => ({
        url: `${BASE}/orders/vendor-proposals/${id}/select`,
        method: "POST",
        body: { reason },
      }),
      transformResponse: unwrap,
      invalidatesTags: (_r, _e, { orderRequestId, purchaseId }) => [
        { type: "VENDOR_PROPOSAL", id: orderRequestId || "ALL" },
        { type: "VENDOR_PROPOSAL", id: "ALL" },
        { type: "PURCHASE_VENDOR_PROPOSAL", id: purchaseId || "ALL" },
        { type: "PURCHASE_VENDOR_PROPOSAL", id: "ALL" },
        ...(purchaseId ? [{ type: "PURCHASE", id: purchaseId }, { type: "PURCHASE", id: "LIST" }] : []),
        ...(orderRequestId
          ? [
              { type: "ORDER-REVIEW", id: orderRequestId },
              { type: "ORDER-REVIEW", id: "MY-LIST" },
              { type: "ORDER-REVIEW", id: "ALL-LIST" },
            ]
          : []),
      ],
    }),

    // ── PO-scoped vendor proposals ───────────────────────────────────────────
    // GET /purchases/{id}/vendor-proposals — every vendor proposal attached
    // directly to this Purchase, entirely independent of whatever proposals
    // exist on the order/group order the PO was created from (different
    // `purchaseId` vs `orderRequestId`/`groupOrderId` scoping — a PO can
    // gather its own quotes after the fact, e.g. to re-price a line before
    // issuing). Same per-row shape as `getVendorProposals`. Named distinctly
    // (not `getVendorProposals`) since RTK Query endpoint names are global
    // across every `injectEndpoints` call sharing this `api` instance and a
    // same-named endpoint would silently lose to whichever loads first
    // (`overrideExisting: false` below).
    getPurchaseVendorProposals: build.query({
      query: (id) => ({ url: `${BASE}/purchases/${id}/vendor-proposals` }),
      transformResponse: unwrap,
      providesTags: (_r, _e, id) => [{ type: "PURCHASE_VENDOR_PROPOSAL", id: id || "ALL" }],
    }),

    // POST /purchases/{id}/vendor-proposals — same request/response shape as
    // `createVendorProposalRequest`, scoped to a Purchase instead of an order/
    // group (`id` comes from the path — never orderRequestId/groupOrderId/
    // purchaseId in the body, the BE 400s if any of those are present).
    createPurchaseVendorProposalRequest: build.mutation({
      query: ({
        id,
        supplierIds,
        newSuppliers,
        title,
        terms,
        requiredDocuments,
        deliveryLocation,
        deliveryDate,
        noteForVendor,
        contactName,
        contactEmail,
        contactPhone,
        pinEnabled,
        items,
        supplierItems,
        expiredAt,
        deliveryFee,
      }) => ({
        url: `${BASE}/purchases/${id}/vendor-proposals`,
        method: "POST",
        body: {
          supplierIds,
          newSuppliers: (newSuppliers || []).map(({ name, taxCode, email }) => ({
            name,
            taxCode,
            email: email || undefined,
          })),
          title,
          terms,
          requiredDocuments: (requiredDocuments || []).map(({ name, mandatory }) => ({
            name,
            mandatory: !!mandatory,
          })),
          deliveryLocation: deliveryLocation || undefined,
          deliveryDate: deliveryDate || undefined,
          noteForVendor: noteForVendor || undefined,
          contactName: contactName || undefined,
          contactEmail: contactEmail || undefined,
          contactPhone: contactPhone || undefined,
          pinEnabled,
          // Exactly one of itemId (catalog) / orderRequestLineId (off-catalog line).
          items: (items || []).map(({ itemId, orderRequestLineId, proposedAmount, note, warrantyMonths, warrantyNote }) => ({
            itemId: itemId ?? undefined,
            orderRequestLineId: orderRequestLineId ?? undefined,
            proposedAmount: proposedAmount ?? undefined,
            note: note || undefined,
            // Omitted = BE keeps the saved value.
            warrantyMonths: warrantyMonths ?? undefined,
            warrantyNote: warrantyNote || undefined,
          })),
          // B4: per-supplier subset of `items`; a supplier without an entry quotes every item.
          supplierItems: supplierItems?.length
            ? supplierItems.map(({ supplierId, items: subset }) => ({
                supplierId,
                items: subset.map(({ itemId, orderRequestLineId }) => ({
                  itemId: itemId ?? undefined,
                  orderRequestLineId: orderRequestLineId ?? undefined,
                })),
              }))
            : undefined,
          // Shown on the proposal/PO only — never part of totalAmount.
          deliveryFee: deliveryFee ?? undefined,
          expiredAt,
        },
      }),
      transformResponse: unwrap,
      // PURCHASE too: an auto-selected proposal changes the PO's vendor/total
      // (and may reroute approval), and PO detail now reads vendorProposals
      // straight off GET /purchases/{id}.
      invalidatesTags: (_r, _e, { id }) => [
        { type: "PURCHASE_VENDOR_PROPOSAL", id: id || "ALL" },
        { type: "PURCHASE_VENDOR_PROPOSAL", id: "ALL" },
        { type: "PURCHASE", id },
        { type: "PURCHASE", id: "LIST" },
        { type: "PROPOSAL_COMPARISON", id: `purchase-${id}` },
      ],
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetPurchasesQuery,
  useGetPurchaseQuery,
  useCreatePurchaseMutation,
  useUpdatePurchaseMutation,
  useGetPurchasePriceReadinessQuery,
  useGetOrderPriceReadinessQuery,
  useUpdatePurchaseProposalPricesMutation,
  useLazyGetPurchaseBudgetImpactQuery,
  useGetPurchaseOrderLinePriceChangesQuery,
  useGetInternalStockAvailabilityQuery,
  useAdjustPurchaseBudgetMutation,
  useSubmitPurchaseMutation,
  usePurchaseActionMutation,
  useResubmitPurchaseMutation,
  useIssuePurchaseMutation,
  useSendPurchaseToVendorMutation,
  useResendPurchaseToVendorMutation,
  useReceivePurchaseMutation,
  useCancelPurchaseMutation,
  useGetPurchaseSuppliersQuery,
  useGetVendorProposalsQuery,
  useGetGroupVendorProposalsQuery,
  useGetGroupOrderLinesQuery,
  useCreateVendorProposalRequestMutation,
  useSelectVendorProposalMutation,
  useSetProposalPreferredMutation,
  useGetProposalComparisonQuery,
  useSaveProposalComparisonMutation,
  useUpdateProposalRequirementsMutation,
  useRevokeVendorProposalMutation,
  useReactivateVendorProposalMutation,
  useUpdateVendorProposalCommunicationHistoryMutation,
  useGetVendorProposalRevisionsQuery,
  useGetPurchaseVendorProposalsQuery,
  useCreatePurchaseVendorProposalRequestMutation,
} = procurementPurchasesApi

export default procurementPurchasesApi
